<template>
	<div v-if="!isHidden" class="shop-purchase-wrapper">
		<AppTextButton
			class="pack-overview-popup__price"
			:type="StatusTypes.SUCCESS"
			:text="formattedPrice"
			:fontSize="36"
			:fontWeight="FontWeights.BOLD"
			:disabled="isButtonDisabled"
			:notification="isPiggyBankCompleted ? '!' : undefined"
			@click="handlePurchase"
		/>

		<span v-if="purchaseLimitText" class="pack-overview-popup__limit">
			{{ purchaseLimitText }}
		</span>
	</div>
</template>

<script lang="ts" setup>
import AppTextButton from "@/components/AppTextButton.vue";
import type { ExtendedPiggyBankOffer } from "@/services/OfferProvider";
import { OfferProvider } from "@/services/OfferProvider";
import { PaymentProvider } from "@/services/PaymentProvider";
import type { OfferAnalytics } from "@/store/usePopupStore";
import type { ActiveProvision } from "@/store/useUserStore";
import useUserStore from "@/store/useUserStore";
import { OfferTemplate, type ShopItem } from "@/types/shop";
import { FontWeights, StatusTypes } from "@/types/ui";
import { formatPrice } from "@/utils/format";
import type { DualPrice } from "@/utils/price";
import { getDualPrice } from "@/utils/price";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed } from "vue";

interface Props {
	offer: ShopItem | ExtendedPiggyBankOffer;
	analytics?: OfferAnalytics | null;
}

const userStore = useUserStore();
const { activeProvisions } = storeToRefs(userStore);

const { offer, analytics = null } = defineProps<Props>();

const formattedPrice = computed((): string => {
	return formatPrice(dualPrice.value[0].value, dualPrice.value[0].currency);
});

const dualPrice = computed((): DualPrice => {
	return getDualPrice(offer.currency || "", offer.retail_price || 0);
});

const purchaseLimitText = computed((): string | null => {
	const limit = offer.attributes?.limit;
	if (limit === undefined) return null;

	const current = offer.num_of_purchases ?? 0;
	return `${translate("ui.limit")} ${current}/${limit}`;
});

const isPiggyBankCompleted = computed((): boolean => {
	if (offer.attributes?.template !== OfferTemplate.PIGGY_BANK) return false;

	if ("questCompleted" in offer) {
		return (
			(offer as Partial<ExtendedPiggyBankOffer>).questCompleted === true
		);
	}

	// fallback
	const extendedList = OfferProvider.extendedPiggyBankOffers.value ?? [];

	const extendedData = extendedList.find(
		(ext: ExtendedPiggyBankOffer): boolean =>
			ext.store_id === offer.store_id,
	);

	return extendedData?.questCompleted === true;
});

const isButtonDisabled = computed((): boolean => {
	if (offer.attributes?.template === OfferTemplate.PIGGY_BANK) {
		return !isPiggyBankCompleted.value;
	}
	return false;
});

const isHidden = computed((): boolean => {
	// IMPORTANT: Unlimited offer, but cannot be repurchased while active
	if (offer.attributes?.template === OfferTemplate.PROVISION) {
		return activeProvisions.value.some(
			(p: ActiveProvision): boolean => p.storeId === offer.store_id,
		);
	}

	return false;
});

const handlePurchase = (): void => {
	if (isButtonDisabled.value) return;

	PaymentProvider.buyPackage(offer.store_id, analytics ?? undefined);
};
</script>

<style lang="scss" scoped>
.shop-purchase-wrapper {
	@include flex-col-center;
	gap: 0.75rem;
}
</style>
